import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useCart } from '../contexts/CartContext';
import StripePaymentForm from '../components/StripePaymentForm';
import { trackCartView } from '../utils/visitorTracking';

import { API_BASE_URL } from '../config/api';

function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { cartItems, removeFromCart, updateQuantity, clearCart, getCartTotal, addToCart } = useCart();
  const [checkoutInfo, setCheckoutInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA'
  });
  const [processing, setProcessing] = useState(false);
  const [shippingConfig, setShippingConfig] = useState({
    freeShippingThreshold: 100.00,
    shippingCost: 10.00,
    freeShippingMessage: "FREE Shipping on orders over $100.00",
    freeShippingSuccessMessage: "You got FREE Shipping!"
  });
  const [stripePromise, setStripePromise] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [paymentIntentError, setPaymentIntentError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Calculate total early so it can be used in useEffect
  const total = getCartTotal();
  
  // Check if all items in cart are digital downloads
  const isDigitalOnly = cartItems.length > 0 && cartItems.every(item => item.type === 'Digital');

  // Track cart view
  useEffect(() => {
    trackCartView();
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/config`);
        if (response.data.success) {
          if (response.data.data.shipping) {
            setShippingConfig(response.data.data.shipping);
          }
          // Initialize Stripe with publishable key
          if (response.data.data.stripe && response.data.data.stripe.publishableKey) {
            const stripeKey = response.data.data.stripe.publishableKey;
            if (stripeKey && stripeKey !== 'pk_test_51...') {
              setStripePromise(loadStripe(stripeKey));
            }
          }
        }
      } catch (err) {
        console.error('Error loading config:', err);
        // Use defaults if config fails to load
      }
    };
    fetchConfig();
  }, []);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAdmin(response.data.success === true);
        } catch (err) {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, []);

  // Restore cart and form data when returning from failed payment
  useEffect(() => {
    // Check for payment error in URL params or location state
    const paymentErrorParam = searchParams.get('payment_error');
    const paymentFailed = location.state?.paymentFailed || false;
    const errorMessage = location.state?.errorMessage || paymentErrorParam;
    
    if (paymentFailed || paymentErrorParam) {
      setPaymentError(errorMessage || 'Payment failed. Please try again.');
      
      // Restore cart items and form data from checkoutOrderData
      const savedOrderData = localStorage.getItem('checkoutOrderData');
      if (savedOrderData) {
        try {
          const orderData = JSON.parse(savedOrderData);
          
          // Restore cart items if cart is empty
          if (cartItems.length === 0 && orderData.items && orderData.items.length > 0) {
            // Ensure all items have a type field (migration for old cart items)
            const migratedItems = orderData.items.map(item => ({
              ...item,
              type: item.type || 'Physical' // Default to Physical if type is missing
            }));
            
            // Set cart directly in localStorage - CartContext will sync on next render
            localStorage.setItem('cart', JSON.stringify(migratedItems));
            
            // Also restore items one by one to update CartContext state immediately
            // This ensures the UI updates without requiring a page reload
            migratedItems.forEach(item => {
              addToCart(item);
            });
          }
          
          // Restore form data from shippingInfo
          if (orderData.shippingInfo || orderData.name || orderData.email) {
            setCheckoutInfo({
              name: orderData.shippingInfo?.name || orderData.name || '',
              email: orderData.shippingInfo?.email || orderData.email || '',
              phone: orderData.shippingInfo?.phone || orderData.phone || '',
              address: orderData.shippingInfo?.address || orderData.address || '',
              city: orderData.shippingInfo?.city || orderData.city || '',
              state: orderData.shippingInfo?.state || orderData.state || '',
              zip: orderData.shippingInfo?.zip || orderData.zip || '',
              country: orderData.shippingInfo?.country || orderData.country || 'USA'
            });
          }
          
          // Clean up checkoutOrderData after restoring
          localStorage.removeItem('checkoutOrderData');
          
          // Clean up URL params
          if (paymentErrorParam) {
            const newUrl = new URL(window.location);
            newUrl.searchParams.delete('payment_error');
            window.history.replaceState({}, '', newUrl);
          }
        } catch (err) {
          console.error('Error restoring checkout data:', err);
        }
      }
    }
  }, [searchParams, location.state, cartItems.length, addToCart, clearCart]);

  // Auto-fill form in development mode (only if no payment error restoration happened)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && !paymentError) {
      // Generate random test number (01-99)
      const testNumber = Math.floor(Math.random() * 99) + 1;
      const testNumberStr = testNumber.toString().padStart(2, '0');
      
      // Random addresses for testing
      const addresses = [
        { address: '123 Main Street', city: 'New York', state: 'NY', zip: '10001' },
        { address: '456 Oak Avenue', city: 'Los Angeles', state: 'CA', zip: '90001' },
        { address: '789 Pine Road', city: 'Chicago', state: 'IL', zip: '60601' },
        { address: '321 Elm Street', city: 'Houston', state: 'TX', zip: '77001' },
        { address: '654 Maple Drive', city: 'Phoenix', state: 'AZ', zip: '85001' }
      ];
      const randomAddress = addresses[Math.floor(Math.random() * addresses.length)];

      // Only auto-fill if form is empty
      if (!checkoutInfo.name && !checkoutInfo.email) {
        setCheckoutInfo({
          name: `Buyer Test${testNumberStr}`,
          email: `doodlingjorge+Test${testNumberStr}@gmail.com`,
          phone: '111-111-1111',
          address: randomAddress.address,
          city: randomAddress.city,
          state: randomAddress.state,
          zip: randomAddress.zip,
          country: 'USA'
        });
      }
    }
  }, [paymentError, checkoutInfo.name, checkoutInfo.email]);

  // Create payment intent when cart loads (to show payment methods)
  // This allows PaymentElement to display payment methods immediately
  useEffect(() => {
    const createPaymentIntent = async () => {
      // Admin test bypass: no Stripe needed. The server's /payment/confirm
      // bypasses Stripe verification for admins, so a local mock id is enough.
      if (isAdmin) {
        setPaymentIntentId(`pi_admin_${Date.now()}`);
        return;
      }

      // Only create if Stripe is configured and we have items in cart
      if (!stripePromise || cartItems.length === 0) {
        return;
      }

      // Calculate shipping: 0 for digital products, otherwise use shipping cost
      const calculatedShipping = isDigitalOnly ? 0 : (total >= shippingConfig.freeShippingThreshold ? 0 : (shippingConfig.shippingCost || 10.00));
      const finalTotal = total + calculatedShipping;
      
      // Stripe minimum is $0.50, so use that if total is too small
      const amountToUse = finalTotal > 0 ? Math.max(finalTotal, 0.50) : 0.50;

      try {
        setPaymentIntentError(null);
        
        // Include admin token if logged in (for test mode)
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.post(`${API_BASE_URL}/payment/create-intent`, {
          amount: amountToUse,
          currency: 'usd',
          metadata: {
            orderItems: JSON.stringify(cartItems),
            // Include name/email if available, otherwise use placeholders
            customerEmail: checkoutInfo.email || 'pending@example.com',
            customerName: checkoutInfo.name || 'Pending Customer'
          }
        }, { headers });

        if (response.data.success) {
          setClientSecret(response.data.clientSecret);
          setPaymentIntentId(response.data.paymentIntentId);
        } else {
          setPaymentIntentError('Failed to initialize payment');
        }
      } catch (err) {
        console.error('Error creating payment intent:', err);
        const errorMessage = err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to initialize payment';
        setPaymentIntentError(errorMessage);
        console.error('Payment intent error details:', {
          error: err.response?.data?.error,
          details: err.response?.data?.details,
          fullError: err.response?.data
        });
      }
    };

    createPaymentIntent();
  }, [stripePromise, total, cartItems, isDigitalOnly, shippingConfig, checkoutInfo.name, checkoutInfo.email, isAdmin]);

  const handleInputChange = (e) => {
    setCheckoutInfo({
      ...checkoutInfo,
      [e.target.name]: e.target.value
    });
  };

  const handlePaymentSuccess = (result) => {
    setPaymentSuccess(true);
    setOrderId(result.orderId);
    
    // Save order details before clearing cart
    const shippingCost = isDigitalOnly ? 0 : (shippingConfig.shippingCost || 10.00);
    const calculatedShipping = isDigitalOnly ? 0 : (total >= shippingConfig.freeShippingThreshold ? 0 : shippingCost);
    const orderDetails = {
      orderId: result.orderId,
      items: cartItems,
      shippingInfo: checkoutInfo,
      subtotal: total,
      shipping: calculatedShipping,
      total: total + calculatedShipping,
      date: new Date().toISOString(),
      freeShipping: isDigitalOnly ? true : (total >= shippingConfig.freeShippingThreshold),
      isDigitalOnly: isDigitalOnly
    };
    
    // Save to localStorage for receipt display
    localStorage.setItem('lastOrder', JSON.stringify(orderDetails));
    
    clearCart();
    // Redirect to success page after 2 seconds
    setTimeout(() => {
      navigate(`/checkout/success?orderId=${result.orderId}`, { state: orderDetails });
    }, 2000);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    // Error is handled by StripePaymentForm component
  };

  // Admin test bypass: place the order directly, skipping Stripe Elements.
  // The server's /payment/confirm bypasses payment verification for admins.
  const handleAdminBypassOrder = async () => {
    if (!paymentIntentId) return;

    setProcessing(true);
    setPaymentError(null);

    const shippingCost = isDigitalOnly ? 0 : (total >= shippingConfig.freeShippingThreshold ? 0 : (shippingConfig.shippingCost || 10.00));
    const orderData = {
      items: cartItems,
      shippingInfo: checkoutInfo,
      name: checkoutInfo.name,
      email: checkoutInfo.email,
      phone: checkoutInfo.phone,
      address: checkoutInfo.address,
      city: checkoutInfo.city,
      state: checkoutInfo.state,
      zip: checkoutInfo.zip,
      country: checkoutInfo.country,
      subtotal: total,
      shipping: shippingCost,
      total: total + shippingCost,
      freeShipping: isDigitalOnly ? true : (total >= shippingConfig.freeShippingThreshold),
      isDigitalOnly
    };

    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API_BASE_URL}/payment/confirm`, {
        paymentIntentId,
        orderData
      }, { headers });

      if (response.data.success) {
        handlePaymentSuccess(response.data);
      } else {
        setPaymentError(response.data.error || 'Order confirmation failed');
      }
    } catch (err) {
      console.error('Admin bypass order error:', err);
      setPaymentError(
        err.response?.data?.details || err.response?.data?.error || err.message || 'Order confirmation failed'
      );
    } finally {
      setProcessing(false);
    }
  };

  // Don't show "Cart is Empty" if payment is processing or successful
  if (cartItems.length === 0 && !paymentSuccess && !processing) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <svg
              className="w-16 h-16 mx-auto text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Add some designs to your cart to get started!</p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Browse Designs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show processing/success message if cart is empty but payment is processing or successful
  if (cartItems.length === 0 && (paymentSuccess || processing)) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            {paymentSuccess ? (
              <>
                <svg
                  className="w-16 h-16 mx-auto text-green-600 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
                {orderId && (
                  <p className="text-gray-600 mb-4">Order ID: {orderId}</p>
                )}
                <p className="text-gray-600 mb-6">Processing your order...</p>
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-sm text-gray-500 mt-4">Redirecting to confirmation page...</p>
              </>
            ) : (
              <>
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h2>
                <p className="text-gray-600 mb-6">Please wait while we process your payment...</p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <Link
            to="/"
            className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Continue Shopping
          </Link>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-8">
          {/* Order Summary */}
          <div className="lg:col-span-2 order-1">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Order Summary</h2>
                {cartItems.length > 0 && (
                  <button
                    onClick={() => clearCart()}
                    className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Clear All
                  </button>
                )}
              </div>
              <div className="space-y-4">
                {cartItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 pb-4 border-b border-gray-200 last:border-0"
                  >
                    <img
                      src={item.imageUrl}
                      alt={item.designTitle}
                      className="w-20 h-20 object-contain rounded border border-gray-200"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{item.designTitle}</h3>
                      <p className="text-sm text-gray-600">{item.formatName}</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        ${item.price.toFixed(2)} each
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(index, item.quantity - 1)}
                          className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                        </button>
                        <span className="w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(index, item.quantity + 1)}
                          className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      </div>
                      <p className="font-semibold text-gray-900 w-20 text-right">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                      <button
                        onClick={() => removeFromCart(index)}
                        className="text-red-600 hover:text-red-700 p-1"
                        title="Remove"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Total */}
          <div className="lg:col-span-1 order-2">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Total</h2>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal:</span>
                  <span>${total.toFixed(2)}</span>
                </div>
                {isDigitalOnly ? (
                  <div className="flex justify-between text-gray-600">
                    <span>Instant Download:</span>
                    <span className="text-green-600 font-semibold">FREE</span>
                  </div>
                ) : (
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping:</span>
                    <span className={total >= shippingConfig.freeShippingThreshold ? 'text-green-600 font-semibold' : 'text-gray-900'}>
                      {total >= shippingConfig.freeShippingThreshold 
                        ? 'FREE' 
                        : `$${(shippingConfig.shippingCost || 10.00).toFixed(2)}`}
                    </span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <div className="flex justify-between text-lg font-bold text-gray-900">
                    <span>Total:</span>
                    <span>${(total + (isDigitalOnly ? 0 : (total >= shippingConfig.freeShippingThreshold ? 0 : (shippingConfig.shippingCost || 10.00)))).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              {isDigitalOnly ? (
                <p className="mt-3 text-center text-sm font-semibold text-green-600">
                  Your digital downloads will be sent to your email after payment.
                </p>
              ) : total >= shippingConfig.freeShippingThreshold ? (
                <p className="mt-3 text-center text-sm font-semibold text-green-600">
                  {shippingConfig.freeShippingSuccessMessage || "You got FREE Shipping!"}
                </p>
              ) : (
                <p className="mt-3 text-center text-sm text-gray-600">
                  {shippingConfig.freeShippingMessage}
                </p>
              )}
            </div>
          </div>

          {/* Shipping Information / Customer Information */}
          <div className="lg:col-span-2 order-3">
            <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 lg:p-6">
              <div className="flex items-center justify-between mb-2 sm:mb-3 lg:mb-4">
                <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900">
                  {isDigitalOnly ? 'Customer Information' : 'Shipping Information'}
                </h2>
                {process.env.NODE_ENV === 'development' && (
                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                    🧪 Auto-filled
                  </span>
                )}
              </div>
              <form onSubmit={(e) => e.preventDefault()} className="space-y-2 sm:space-y-3 lg:space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5 sm:mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={checkoutInfo.name}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5 sm:mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={checkoutInfo.email}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5 sm:mb-1">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={checkoutInfo.phone}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5 sm:mb-1">
                    Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    required
                    value={checkoutInfo.address}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5 sm:mb-1">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={checkoutInfo.city}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5 sm:mb-1">
                      State *
                    </label>
                    <input
                      type="text"
                      name="state"
                      required
                      value={checkoutInfo.state}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5 sm:mb-1">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      name="zip"
                      required
                      value={checkoutInfo.zip}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-0.5 sm:mb-1">
                    Country *
                  </label>
                  <input
                    type="text"
                    name="country"
                    required
                    value={checkoutInfo.country}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </form>
            </div>
          </div>

          {/* Payment Section */}
          <div className="lg:col-span-2 order-4">
            {paymentSuccess ? (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-center">
                  <svg
                    className="w-16 h-16 mx-auto text-green-600 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
                  <p className="text-gray-600 mb-4">Order ID: {orderId}</p>
                  <p className="text-sm text-gray-500">Redirecting to confirmation page...</p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Information</h2>
                {!stripePromise ? (
                  <div className="text-center py-4">
                    <p className="text-gray-600 mb-2">Loading payment form...</p>
                    <p className="text-sm text-gray-500">
                      Please configure Stripe keys in config.json
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Admin Payment Bypass Notice */}
                    {isAdmin && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <h3 className="text-sm font-semibold text-blue-800 mb-2">🔧 Admin Mode - Payment Bypass Enabled</h3>
                        <p className="text-xs text-blue-700">
                          You are logged in as an admin. Payment will be automatically bypassed for testing purposes.
                        </p>
                      </div>
                    )}
                    {/* Development Mode Test Card Helper */}
                    {process.env.NODE_ENV === 'development' && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                        <h3 className="text-sm font-semibold text-yellow-800 mb-2">🧪 Development Mode - Test Card Details</h3>
                        <div className="text-xs text-yellow-700 space-y-1">
                          <p><strong>Card Number:</strong> 4242 4242 4242 4242</p>
                          <p><strong>Exp Date:</strong> 04/34</p>
                          <p><strong>CVC:</strong> 424</p>
                          <p><strong>ZIP Code:</strong> 12345</p>
                        </div>
                      </div>
                    )}
                    {/* Show error if payment failed and user was redirected back */}
                    {paymentError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-red-800 mb-1">Payment Failed</p>
                            <p className="text-sm text-red-600">{paymentError}</p>
                            <p className="text-xs text-red-500 mt-2">Your cart items have been restored. Please review and try again.</p>
                          </div>
                          <button
                            onClick={() => setPaymentError(null)}
                            className="text-red-400 hover:text-red-600 transition-colors"
                            aria-label="Dismiss error"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Show error if payment intent creation failed */}
                    {paymentIntentError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                        <p className="text-sm text-red-600">{paymentIntentError}</p>
                      </div>
                    )}

                    {/* Admin test bypass: skip Stripe Elements entirely */}
                    {isAdmin ? (
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={handleAdminBypassOrder}
                          disabled={!paymentIntentId || processing || !checkoutInfo.name || !checkoutInfo.email}
                          className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {processing
                            ? 'Placing order...'
                            : `Place Order (Admin Bypass) — $${(total + (isDigitalOnly ? 0 : (total >= shippingConfig.freeShippingThreshold ? 0 : (shippingConfig.shippingCost || 10.00)))).toFixed(2)}`}
                        </button>
                        {!paymentIntentId && (
                          <p className="text-xs text-gray-500 text-center">Preparing order...</p>
                        )}
                      </div>
                    ) : !clientSecret ? (
                      <div className="text-center py-4">
                        {!checkoutInfo.name || !checkoutInfo.email ? (
                          <p className="text-gray-600 mb-2">Please fill in your name and email above to see available payment methods</p>
                        ) : (
                          <>
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <p className="mt-2 text-sm text-gray-600">Loading payment methods...</p>
                          </>
                        )}
                      </div>
                    ) : (
                      <Elements 
                        stripe={stripePromise} 
                        options={{ 
                          clientSecret,
                          appearance: {
                            theme: 'stripe',
                          },
                          defaultValues: {
                            billingDetails: {
                              name: checkoutInfo.name,
                              email: checkoutInfo.email,
                              phone: checkoutInfo.phone,
                              address: {
                                line1: checkoutInfo.address,
                                city: checkoutInfo.city,
                                state: checkoutInfo.state,
                                postal_code: checkoutInfo.zip,
                                country: checkoutInfo.country === 'USA' ? 'US' : checkoutInfo.country
                              }
                            }
                          }
                        }}
                      >
                        <StripePaymentForm
                          amount={total + (isDigitalOnly ? 0 : (total >= shippingConfig.freeShippingThreshold ? 0 : (shippingConfig.shippingCost || 10.00)))}
                          orderData={{
                            items: cartItems,
                            shippingInfo: checkoutInfo,
                            name: checkoutInfo.name,
                            email: checkoutInfo.email,
                            phone: checkoutInfo.phone,
                            address: checkoutInfo.address,
                            city: checkoutInfo.city,
                            state: checkoutInfo.state,
                            zip: checkoutInfo.zip,
                            country: checkoutInfo.country,
                            subtotal: total,
                            shipping: isDigitalOnly ? 0 : (total >= shippingConfig.freeShippingThreshold ? 0 : (shippingConfig.shippingCost || 10.00)),
                            total: total + (isDigitalOnly ? 0 : (total >= shippingConfig.freeShippingThreshold ? 0 : (shippingConfig.shippingCost || 10.00))),
                            freeShipping: isDigitalOnly ? true : (total >= shippingConfig.freeShippingThreshold),
                            isDigitalOnly: isDigitalOnly
                          }}
                          onSuccess={handlePaymentSuccess}
                          onError={handlePaymentError}
                          onProcessingChange={setProcessing}
                          disabled={!checkoutInfo.name || !checkoutInfo.email || !!paymentIntentError}
                        />
                      </Elements>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;

